import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import SystemIcon from '../ShortStatus/SystemIcon';
import Button from '../../../components/CustomButtons/ButtonTS';
import CancelIcon from '@material-ui/icons/Cancel';
import Card from '../../../components/Card/CardTS';
import CardBody from '../../../components/Card/CardBody';
import CardHeader from '../../../components/Card/CardHeaderTS';
import CardIcon from '../../../components/Card/CardIcon';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { closeSweetAlertStyle } from '../../helpers/CustomStyles';
import { Account } from '../../helpers/interfaces';
import utils from '../../helpers/GlobalVariables';
import { deleteUser, closeDeleteUserNotification } from '../../stores/actions/installerActions';
import { getVisibleAccounts } from '../../stores/actions/actions';
import Snackbar from '../../../components/Snackbar/Snackbar';
import Loading from '../Loading/Loading';

interface RemoveUserConfirmationProps {
	account: Account;
	deleteUserSucceeded: boolean;
	deleteUserFailed: boolean;
	mobileView: boolean;
	hideRemoveUserConfirmation: () => void;
	deleteUser: (systemId: number) => void;
	closeDeleteUserNotification: () => void;
	getVisibleAccounts: () => void;
}

const closeButtonStyle = {
	border: '1px solid #00acc1',
};

const RemoveUserConfirmationCard = ({
	account,
	deleteUserSucceeded,
	deleteUserFailed,
	mobileView,
	hideRemoveUserConfirmation,
	deleteUser,
	closeDeleteUserNotification,
	getVisibleAccounts,
}: RemoveUserConfirmationProps) => {
	const [popupTimeoutEnabled, setPopupTimeoutEnabled] = useState(false);
	const [isLoaderRender, setIsLoaderRender] = useState(false);
	useEffect(() => {
		closeDeleteUserNotification();
	}, [closeDeleteUserNotification]);

	useEffect(() => {
		if (deleteUserSucceeded) {
			setPopupTimeoutEnabled(true);
		}
		if (deleteUserSucceeded || deleteUserFailed) {
			const timeoutNotification = setTimeout(() => {
				closeDeleteUserNotification();
				setIsLoaderRender(false);
			}, 6000);
			return () => {
				clearTimeout(timeoutNotification);
			};
		}
	}, [closeDeleteUserNotification, deleteUserSucceeded, deleteUserFailed]);

	useEffect(() => {
		if (popupTimeoutEnabled) {
			const closeWindowTimeout = setTimeout(() => {
				setPopupTimeoutEnabled(false);
				getVisibleAccounts();
				hideRemoveUserConfirmation();
			}, 6000);
			return () => {
				clearTimeout(closeWindowTimeout);
			};
		}
	}, [getVisibleAccounts, popupTimeoutEnabled, hideRemoveUserConfirmation]);

	const closeButtonHover = (e) => {
		const button = e.target;
		button.style.backgroundColor = 'rgba(0, 172, 193, 0.1)';
	};

	const closeButtonLeave = (e) => {
		const button = e.target;
		button.style.backgroundColor = '#ffffff';
	};

	const handleDeleteUser = () => {
		deleteUser(account.id);
	};

	const handleCloseNotification = () => {
		setIsLoaderRender(false);
		closeDeleteUserNotification();
	};

	const renderDesktopNotifications = (): JSX.Element => {
		if (mobileView) return null;

		const color = deleteUserSucceeded ? 'success' : 'danger';
		const message = deleteUserSucceeded
			? 'User was deleted successfully.'
			: 'You do not have the permissions to remove this user.';
		const isOpen = deleteUserSucceeded || deleteUserFailed ? true : false;
		return (
			<Snackbar
				close={true}
				closeNotification={handleCloseNotification}
				color={color}
				message={message}
				open={isOpen}
				place='tc'
			/>
		);
	};

	const renderMobileNotifications = () => {
		const headerStyle = utils.getHeaderStyle(deleteUserFailed);
		if (!mobileView) return null;
		if (deleteUserFailed || deleteUserSucceeded) {
			return (
				<GridItem xs={12}>
					<h5
						className={deleteUserFailed ? 'notification-open-create-system-fail' : 'notification-open-create-system-success'}
						style={headerStyle}
					>
						{deleteUserFailed
							? 'You do not have the permissions to remove this user.'
							: 'User deleted successfully.'}
					</h5>
				</GridItem>
			);
		}
	};

	const handleRemoveButtonClicked = () => {
		setIsLoaderRender(true);
		handleDeleteUser();
	};

	return (
		<div>
			<GridContainer>
				<GridItem xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
					<Button
						data-testid='cancelButton'
						simple
						justIcon
						color='info'
						style={closeSweetAlertStyle}
						onClick={hideRemoveUserConfirmation}
					>
						<CancelIcon style={{ width: '20px', height: '20px' }} />
					</Button>
				</GridItem>
			</GridContainer>
			<Card chart style={{ overflow: 'visible', marginTop: '10px', marginBottom: '0' }} className='node-edit-card'>
				<CardHeader color='info' icon>
					<CardIcon color='info'>
						<SystemIcon />
					</CardIcon>
					<h3 style={utils.sweetAlertHeaderStyle} className='remove-system-header'>
						{`${account.name} ${account.email}`}
					</h3>
				</CardHeader>
				<CardBody>
					<GridContainer>
						<GridItem xs={12}>
							<h5>This operation is irreversible.<br />Are you sure you want to delete this user?</h5>
						</GridItem>
						{renderMobileNotifications()}
						<GridItem xs={12} style={{ margin: '15px 0 0 0 ' }}>
							<div style={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
								<Button
									className='deleteUserCloseButton'
									simple
									color='info'
									style={closeButtonStyle}
									onClick={hideRemoveUserConfirmation}
									onMouseEnter={(e) => closeButtonHover(e)}
									onMouseLeave={(e) => closeButtonLeave(e)}
								>
									cancel
								</Button>
								<Button
									style={{ backgroundColor: isLoaderRender ? '#ccc' : '#f44336' }}
									onClick={handleRemoveButtonClicked}
									disabled={isLoaderRender}
								>
									{isLoaderRender ? <Loading transform={1} height={17} width={48} color='darkGray' /> : 'remove'}
								</Button>
							</div>
						</GridItem>
					</GridContainer>
				</CardBody>
			</Card>
			{renderDesktopNotifications()}
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		systems: state.dashboard.systems,
		deleteUserSucceeded: state.installer.deleteUserSucceeded,
		deleteUserFailed: state.installer.deleteUserFailed,
		mobileView: state.dashboard.mobileView,
	};
};

const actions = {
	deleteUser,
	closeDeleteUserNotification,
	getVisibleAccounts,
};

export default connect(mapStateToProps, actions)(RemoveUserConfirmationCard);
